/**
 * <shipping-options-popup>
 *
 * The "Faster Shipping Options" modal on the PDP. The trigger replaces the plain
 * shipping-page link when the merchant enables the popup. Arrival dates are
 * computed with the shared window.EddShipping engine (assets/edd-shipping.js).
 *
 *   Standard row   -> min/max range, delivery-strip logic
 *                     (data-sop-standard, data-std-min/max-days, data-cutoff-hour)
 *   Express/Overnight (data-sop-expedited, data-sop-days) -> single arrival date =
 *                     expedited ship date (Mon-Fri, data-expedited-cutoff-hour) plus
 *                     that row's BUSINESS days.
 *
 * Sub-lines (data-sop-subline) show only when an expedited order could still ship
 * today (a weekday, pre-cutoff); otherwise they stay hidden.
 *
 * Close via: close icon, Continue Shopping button, backdrop click, or Escape.
 */
if (!customElements.get("shipping-options-popup")) {
  customElements.define(
    "shipping-options-popup",
    class ShippingOptionsPopup extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.trigger = this.querySelector("[data-sop-open]");
        this.overlay = this.querySelector("[data-sop-overlay]");
        this.panel = this.querySelector('[role="dialog"]');
        if (!this.trigger || !this.overlay || !this.panel) return;
        this.initialized = true;

        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.onKeyup = this.onKeyup.bind(this);

        this.trigger.addEventListener("click", this.open);
        this.overlay
          .querySelectorAll("[data-sop-close]")
          .forEach((el) => el.addEventListener("click", this.close));
        // Backdrop click closes, but only when the overlay itself is clicked
        // (not a click that bubbles up from inside the panel).
        this.overlay.addEventListener("click", (event) => {
          if (event.target === this.overlay) this.close();
        });
        this.overlay.addEventListener("keyup", this.onKeyup);

        // Portal the overlay to <body>. It is otherwise rendered inside the
        // sticky product-info column (.product__info-container--sticky), whose
        // stacking context traps it below the header/footer no matter its
        // z-index. Carry the themed custom properties across since the overlay
        // no longer inherits them from this host once it moves.
        var bg = this.style.getPropertyValue("--pop-bg");
        var text = this.style.getPropertyValue("--pop-text");
        if (bg) this.overlay.style.setProperty("--pop-bg", bg);
        if (text) this.overlay.style.setProperty("--pop-text", text);
        document.body.appendChild(this.overlay);

        // Deep link: open when the URL carries #edd, on load or on a later
        // hash change (e.g. an in-page <a href="#edd">).
        this.onHashChange = this.onHashChange.bind(this);
        window.addEventListener("hashchange", this.onHashChange);
        this.onHashChange();
      }
      onHashChange() {
        if (window.location.hash.toLowerCase() === "#edd") this.open();
      }
      // The overlay was portaled to <body>, so it does not leave with the host
      // when the section unloads (e.g. a theme-editor reload). Remove it here and
      // release the scroll lock so overlays never orphan or leave the page frozen.
      disconnectedCallback() {
        if (this.onHashChange) window.removeEventListener("hashchange", this.onHashChange);
        if (this.overlay && this.overlay.parentNode) {
          this.overlay.parentNode.removeChild(this.overlay);
        }
        document.body.classList.remove("overflow-hidden");
      }

      open(event) {
        if (event) event.preventDefault();
        // Recompute every open so a long-lived page reflects the current PT time
        // (crossing the cutoff or midnight) rather than a value cached on first open.
        this.populateDates();
        this.overlay.hidden = false;
        this.trigger.setAttribute("aria-expanded", "true");
        document.body.classList.add("overflow-hidden");
        if (typeof trapFocus === "function") trapFocus(this.panel);
      }

      close() {
        this.overlay.hidden = true;
        this.trigger.setAttribute("aria-expanded", "false");
        document.body.classList.remove("overflow-hidden");
        if (typeof removeTrapFocus === "function") removeTrapFocus(this.trigger);
        // Drop #edd so the same link can reopen the popup (hashchange only
        // fires when the hash actually changes). replaceState avoids a scroll jump.
        if (window.location.hash.toLowerCase() === "#edd") {
          history.replaceState(null, "", window.location.pathname + window.location.search);
        }
      }

      onKeyup(event) {
        if (event.code && event.code.toUpperCase() === "ESCAPE") this.close();
      }

      // Fill the Arrives column. Called on every open (cheap) so the dates track
      // the current PT time. Without the shared engine the server-rendered
      // placeholders are left in place.
      populateDates() {
        const edd = window.EddShipping;
        if (!edd) return;

        try {
          const holidays = edd.parseHolidays(this.dataset.holidays);
          const now = edd.nowInPacific();
          const nowYear = now.getFullYear();

          this.populateStandard(edd, now, nowYear, holidays);
          this.populateExpedited(edd, now, nowYear, holidays);
          this.createEDDJson()
        } catch (e) {
          // Fail silently: server-rendered placeholders remain.
        }
      }

      // Standard row: min-max range, identical to the delivery strip
      // (Tue-Fri ship, standard cutoff, calendar days + peak buffer).
      populateStandard(edd, now, nowYear, holidays) {
        const standardEl = this.overlay.querySelector("[data-sop-standard]");
        if (!standardEl) return;

        const peakEnabled = this.dataset.peakEnabled === "true";
        const peakDays = peakEnabled ? parseInt(this.dataset.peakDays, 10) || 0 : 0;
        const cfg = edd.shipConfig(this.dataset, holidays);

        const ship = edd.shipDate(now, edd.parseHour(this.dataset.cutoffHour, 10), cfg);
        const min = (parseInt(this.dataset.stdMinDays, 10) || 0) + peakDays;
        const max = (parseInt(this.dataset.stdMaxDays, 10) || 0) + peakDays;
        const low = edd.addDeliveryDays(ship, min, cfg);
        const high = edd.addDeliveryDays(ship, max, cfg);
        const crossYear =
          low.getFullYear() !== nowYear || high.getFullYear() !== nowYear;
        standardEl.replaceChildren(
          this.el("em", "Estimated "),
          document.createTextNode(
            edd.formatDate(low, crossYear) + " – " + edd.formatDate(high, crossYear)
          )
        );
        // console.log("Standard arrives:", edd.formatDate(low, crossYear), "–", edd.formatDate(high, crossYear));
        window.eddDates = window.eddDates || [];
        window.eddDates.push({method: 'standard', label: edd.formatDate(low, crossYear) + " – " + edd.formatDate(high, crossYear)});
      }

      // Express / Overnight: single date = expedited ship date (Mon-Fri, own
      // cutoff) + that row's business days. Sub-lines only show when an order
      // placed now could still ship today.
      populateExpedited(edd, now, nowYear, holidays) {
        const cutoff = edd.parseHour(this.dataset.expeditedCutoffHour, 13);
        // Same config as Standard so a peak-season Saturday counts as an expedited
        // business day too (excludeSundays is unused by the business-day helpers).
        const cfg = edd.shipConfig(this.dataset, holidays);
        const ship = edd.expeditedShipDate(now, cutoff, cfg);

        this.overlay.querySelectorAll("[data-sop-expedited]").forEach((cell) => {
          const days = parseInt(cell.dataset.sopDays, 10) || 0;
          const d = edd.addBusinessDays(ship, days, cfg);
          const crossYear = d.getFullYear() !== nowYear;
          cell.replaceChildren(this.el("strong", edd.formatDate(d, crossYear)));
          // console.log("Expedited arrives:", edd.formatDate(d, crossYear), "for", days, "days");
          window.eddDates = window.eddDates || [];
          window.eddDates.push({method: 'expedited', label: edd.formatDate(d, crossYear)});
        });

        const canShipToday = edd.canShipTodayExpedited(now, cutoff, cfg);
        this.overlay.querySelectorAll("[data-sop-subline]").forEach((node) => {
          node.hidden = !canShipToday;
        });
      }

      el(tag, text) {
        const node = document.createElement(tag);
        node.textContent = text;
        return node;
      }
      createEDDJson() {
        if (!window.eddDates) return;
        // console.log("EDD JSON-LD:", window.eddDates);
        const eddJson = document.createElement("script");
        eddJson.type = "application/ld+json";
        eddJson.id = "edd-json-ld";
        // eddJson.dataset.eddDates = JSON.stringify(window.eddDates); 
        eddJson.textContent = JSON.stringify(window.eddDates);
        document.head.appendChild(eddJson);
        // console.log('EDD JSON-LD script added to head', eddJson);
      }
    }
  );
}
